/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import {
    Button,
    DatePicker,
    FlexCol,
    FlexRow,
    Icon,
    Select,
    Text,
    VisualOnlyLabel,
} from 'sarsaparilla';

import { gnoment, gnomentToCalendarDate, calendarDateToGnoment } from 'shared-ui/gnoment';
import ExtendedStay from './ExtendedStay';
import {
    createAvailabilityRangeMap,
    hasRule,
    readRuleValue,
} from '../../utils/permitUtilsNew';
import { getClassName } from '../../utils/validationUtils';
import { isExtendedStayPresent } from '../../utils/availabilityUtils';
import {
    AVAILABILITY_KEY_MAP_FORMAT,
    EXIT_POINT_NAMES,
    MONTH_DAY_YEAR_NUM_FORMAT,
} from '../../constants';

const propTypes = {
    onChangeStartDate: PropTypes.func.isRequired,
    displayValidationError: PropTypes.bool,
    division: PropTypes.any,
    handleLaunchDetailChange: PropTypes.func,
    isDisabled: PropTypes.bool,
    isStartDateChangeAllowed: PropTypes.bool,
    issuance: PropTypes.shape({ id: PropTypes.string }),
    launchDetail: PropTypes.any,
    permit: PropTypes.any,
    validation: PropTypes.shape({}),
};

const defaultProps = {
    division: {},
    isDisabled: false,
    issuance: {},
    permit: {},
};

export default class EntryExitDates extends React.PureComponent {
    getTotalDays() {
        const { entryDate, exitDate } = this.props.launchDetail;
        if (exitDate) {
            return exitDate.diff(entryDate, 'days') + 1;
        }
        return 0;
    }

    handleExtendedStayChange = (isExtended) => {
        const newDetail = { ...this.props.launchDetail };
        newDetail.isExtended = isExtended;

        const exitLimit = this.computeExitLimit(
            newDetail.entryDate,
            this.props.division.id
        );
        if (newDetail.exitDate && newDetail.exitDate.isAfter(exitLimit)) {
            newDetail.exitDate = exitLimit;
        }
        this.props.handleLaunchDetailChange(newDetail);
    };

    handleExitDateChange = (exitDate) => {
        this.props.handleLaunchDetailChange({ ...this.props.launchDetail, exitDate });
    };

    handleExitLocationChange = ({ target: { value } }) =>
        this.props.handleLaunchDetailChange({
            ...this.props.launchDetail,
            exitLocationId: value,
        });

    computeStayLimit = (divisionId) => {
        const daysToSeconds = 24 * 60 * 60;

        let stayLimit = 365 * daysToSeconds;
        if (hasRule('StayLimitNonLottery', this.props.permit, divisionId)) {
            stayLimit = readRuleValue(
                'StayLimitNonLottery',
                this.props.permit,
                divisionId
            );
        } else if (hasRule('StayLimit', this.props.permit, divisionId)) {
            stayLimit = readRuleValue('StayLimit', this.props.permit, divisionId);
        }

        if (
            hasRule('StayLimitOverLengthInDays', this.props.permit, divisionId) &&
            this.props.launchDetail.isExtended
        ) {
            stayLimit +=
                readRuleValue(
                    'StayLimitOverLengthInDays',
                    this.props.permit,
                    divisionId
                ) * daysToSeconds;
        }
        return stayLimit;
    };

    computeExitLimit = (entryDate, divisionId) => {
        const stayLimit = this.computeStayLimit(divisionId);
        return gnoment(entryDate).utc().clone().add(stayLimit, 'seconds').add(-1, 'days');
    };

    isEmptyObject(obj) {
        return (
            obj === null ||
            typeof obj === 'undefined' ||
            (Object.keys(obj).length === 0 && obj.constructor === Object)
        );
    }

    renderChangeStartDate() {
        return (
            <Button
                appearance="link"
                iconBeforeElement={<Icon iconName="edit" />}
                onClick={this.props.onChangeStartDate}
            >
                I need to make a change
            </Button>
        );
    }

    renderExtendedStay() {
        if (isExtendedStayPresent(this.props.permit)) {
            return (
                <ExtendedStay
                    selected={this.props.launchDetail.isExtended}
                    onSelectionChange={this.handleExtendedStayChange}
                />
            );
        }
        return undefined;
    }

    renderExitDateCalendar(entryDate, exitDate) {
        const {
            issuance: { id },
            validation,
            displayValidationError,
        } = this.props;
        const exitDateFailValidation = getClassName(
            get(validation, `${id}_end_date`),
            displayValidationError
        );

        const exitLimit = this.computeExitLimit(entryDate, this.props.division.id);
        const datesMap = createAvailabilityRangeMap(entryDate, exitLimit);
        return (
            <div className={`${exitDateFailValidation}`}>
                <DatePicker
                    label="Exit Date"
                    isRequired
                    value={gnomentToCalendarDate(
                        this.isEmptyObject(exitDate) ? null : gnoment(exitDate).utc()
                    )}
                    showClearDates={false}
                    onChange={(date) =>
                        this.handleExitDateChange(calendarDateToGnoment(date))
                    }
                    isDateUnavailable={(day) =>
                        !datesMap[
                            calendarDateToGnoment(day)
                                .utc()
                                .format(AVAILABILITY_KEY_MAP_FORMAT)
                        ]
                    }
                    placeholderValue={gnomentToCalendarDate(entryDate)}
                />
            </div>
        );
    }

    renderReadOnly = (label, value) => (
        <div>
            <VisualOnlyLabel isRequired>{label}</VisualOnlyLabel>
            <Text color="gray">{value}</Text>
        </div>
    );

    render() {
        const { division, isDisabled, launchDetail } = this.props;
        const entryDate = gnoment(launchDetail.entryDate).utc();
        const exitDate = launchDetail.exitDate
            ? gnoment(launchDetail.exitDate).utc()
            : undefined;

        return (
            <div className="boundarywaters-entry-exit-dates">
                <FlexRow>
                    <FlexCol>
                        <h2 className="h5">Entry & Exit details</h2>
                        {!isDisabled && (
                            <Text>
                                With your entry date chosen, select your exit date and
                                exit location.
                            </Text>
                        )}
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol md="auto">
                        {this.renderReadOnly(
                            'Entry Date',
                            entryDate.format(MONTH_DAY_YEAR_NUM_FORMAT)
                        )}
                        {this.props.isStartDateChangeAllowed &&
                            this.renderChangeStartDate()}
                    </FlexCol>
                    <FlexCol md="auto">
                        {this.renderReadOnly('Entry Point', division.name || '')}
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol md="auto">
                        {isDisabled
                            ? this.renderReadOnly(
                                  'Exit Date',
                                  exitDate.format(MONTH_DAY_YEAR_NUM_FORMAT)
                              )
                            : this.renderExitDateCalendar(entryDate, exitDate)}
                    </FlexCol>
                    <FlexCol md="auto">
                        <Select
                            id="exit-point"
                            isDisabled={isDisabled}
                            label="Exit Point"
                            onChange={this.handleExitLocationChange}
                            options={EXIT_POINT_NAMES.map((value) => ({
                                label: value,
                                value,
                            }))}
                            placeholder="Select an Exit Point"
                            value={launchDetail.exitLocationId || ''}
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol md="auto">
                        {this.renderReadOnly('Total Days', this.getTotalDays())}
                    </FlexCol>
                    <FlexCol md="auto">{this.renderExtendedStay()}</FlexCol>
                </FlexRow>
                <hr />
            </div>
        );
    }
}

EntryExitDates.propTypes = propTypes;
EntryExitDates.defaultProps = defaultProps;
